import React, { useEffect, useState } from "react";
import { Box, Grid, Container, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useCart } from "../../context/CartContext";
import api from "../../services/api";
import CheckoutStep from "./CheckoutStep";
import DeliveryMethodForm from "./DeliveryMethodForm";
import DateTimePicker from "./DateTimePicker";
import ContactInfoForm from "./ContactInfoForm";
import PaymentForm from "./PaymentForm/PaymentForm";
import SummarySidebar from "./SummarySidebar";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import normalizeCartItemsForOrder from "../../utils/normalizeCartItemsForOrder";
import { useAppConfig } from "../../context/AppConfigContext";
import CouponCodeInput from "./CouponCodeInput";
import { getUTCDateFromParts } from "../../utils/dates/getUTCDateFromParts";
import { useSnackbarAlert } from "../../hooks/useSnackbarAlert";
import { useAuth } from "../../context/AuthContext";

const Checkout = () => {
  const [activeStep, setActiveStep] = useState(1);
  const [deliveryMethod, setDeliveryMethod] = useState("");
  const [selectedLocation, setSelectedLocation] = useState("");
  const [address, setAddress] = useState("");
  const [isDeliveryValid, setIsDeliveryValid] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState("");
  const [contactInfo, setContactInfo] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
  });
  const [acknowledgedPolicy, setAcknowledgedPolicy] = useState(false);
  const [isContactFormValid, setIsContactFormValid] = useState(false);
  const [couponError, setCouponError] = useState("");
  const { user, isAuthenticated } = useAuth();
  const isLoggedInUser = !!user?._id && isAuthenticated;

  const {
    cartSubtotal,
    appliedCoupon,
    setAppliedCoupon,
    activeDiscounts,
    cartDiscounts,
    fullDiscountRules,
    taxAmount,
    cartTotal,
    discountedCartItems,
    totalDiscount,
    clearCart,
  } = useCart();

  const { config } = useAppConfig();
  const snackbar = useSnackbarAlert();
  const navigate = useNavigate();

  const isDelivery = deliveryMethod === "delivery";
  const isPickup = deliveryMethod === "pickup";
  const deliveryFee = isDelivery ? config?.delivery?.deliveryFee || 0 : 0;
  const isStep1Complete = isPickup
    ? !!selectedLocation
    : !!address && isDeliveryValid;
  const isStep2Complete = isStep1Complete && !!selectedDate && !!selectedTime;
  const isStep3Complete = isStep2Complete && isContactFormValid;
  const totalAmount = cartTotal + deliveryFee;

  useEffect(() => {
    document.title = "Checkout";
    if (window.gtag && discountedCartItems.length > 0) {
      window.gtag("event", "begin_checkout", {
        currency: "CAD",
        value: cartTotal,
        items: discountedCartItems.map((item) => ({
          item_id: item._id,
          item_name: item.displayName || item.name,
          quantity: item.quantity,
          price: item.unitPrice,
          item_category: item.category || "Product",
        })),
      });
    }

    if (window.fbq && discountedCartItems.length > 0) {
      window.fbq("track", "InitiateCheckout", {
        content_ids: discountedCartItems.map((item) => item._id),
        content_type: "product",
        value: cartTotal,
        currency: "CAD",
      });
    }

    if (window.gtag && discountedCartItems.length > 0) {
      window.gtag("event", "begin_checkout", {
        currency: "CAD",
        value: cartTotal,
        items: discountedCartItems.map((item) => ({
          item_id: item._id,
          item_name: item.displayName || item.name,
          quantity: item.quantity,
          price: item.unitPrice,
          item_category: item.category || "Product",
        })),
      });
    }
  }, []);

  useEffect(() => {
    if (isStep1Complete && activeStep === 1) setActiveStep(2);
  }, [isStep1Complete]);

  useEffect(() => {
    if (isStep2Complete && activeStep === 2) setActiveStep(3);
  }, [isStep2Complete]);

  useEffect(() => {
    if (isStep3Complete && activeStep === 3) setActiveStep(4);
  }, [isStep3Complete]);

  useEffect(() => {
    if (deliveryMethod === "pickup") {
      setAddress("");
    } else if (deliveryMethod === "delivery") {
      setSelectedLocation("");
    }
  }, [deliveryMethod]);

  const handleApplyCoupon = (code) => {
    const match = activeDiscounts.find(
      (d) => d.couponCode?.toUpperCase() === code.toUpperCase()
    );
    if (!match) {
      setCouponError("Invalid or expired coupon code.");
      setAppliedCoupon("");
    } else {
      setCouponError("");
      setAppliedCoupon(code.toUpperCase());
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon("");
    setCouponError("");
  };

  // helper (optional, keeps money clean)
  const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

  const buildOrderPayload = ({
    paymentMeta = {},
    metaExtra = {},
    receiptNumber = null,
  } = {}) => {
    // 1) use discounted items so each line includes final prices + discountsApplied
    const items = normalizeCartItemsForOrder(discountedCartItems);

    // 2) totals (keep your semantics: subtotal = pre-discount)
    const subtotal = round2(cartSubtotal);
    const tax = round2(taxAmount);
    const delivery = round2(deliveryFee);
    const total = round2(cartTotal + deliveryFee);
    const totalDiscountRounded = round2(totalDiscount);

    return {
      items, // 👈 now includes original prices + discountsApplied per item
      customer: contactInfo,
      fulfillment: {
        method: deliveryMethod,
        date: getUTCDateFromParts({
          year: selectedDate.year(),
          month: selectedDate.month() + 1,
          day: selectedDate.date(),
        }),
        time: selectedTime,
        location: isPickup ? selectedLocation : address,
      },
      payment: {
        subtotal, // pre-discount
        tax,
        deliveryFee: delivery,
        total, // final amount (items after discount + tax + delivery)
        paid: true,
        totalDiscount: totalDiscountRounded,
        ...paymentMeta,
        ...(receiptNumber && { receiptNumber }),
      },
      discounts: cartDiscounts || [], // order-level summary stays
      meta: {
        source: "online",
        userRole: isLoggedInUser ? "user" : "guest",
        refundPolicyAgreed: metaExtra?.acknowledgedCancellationPolicy === true,
        createdAt: new Date(),
        ...metaExtra,
      },
      sessionInfo: {
        userAgent: navigator.userAgent,
        ip: "",
        device: navigator.platform,
      },
      internal: { discountMetadata: fullDiscountRules }, // raw rules for audit
      subscription: {},
      timeline: [
        {
          type: "order_created",
          timestamp: new Date(),
          actor: isLoggedInUser ? "user" : "guest",
          notes: "Initial order submission",
        },
      ],
    };
  };

  const handlePaymentSuccess = async (paymentResult) => {
    try {
      if (paymentResult.type === "stripe") {
        const { paymentIntent } = paymentResult;
        const { data: stripeMeta } = await api.get(
          `/payments/finalize/${paymentIntent.id}`
        );

        const paymentMeta = {
          paymentStatus: stripeMeta.status,
          stripePaymentIntent: stripeMeta.stripePaymentIntent,
          stripeReceiptUrl: stripeMeta.stripeReceiptUrl,
          stripeReceiptNumber: stripeMeta.stripeReceiptNumber,
          stripeCustomerId: stripeMeta.stripeCustomerId,
          stripeChargeId: stripeMeta.stripeChargeId,
          paymentMethod: {
            type: stripeMeta.paymentMethod.type || "stripe_card",
            brand: stripeMeta.paymentMethod.brand || null,
            last4: stripeMeta.paymentMethod.last4 || null,
          },
        };

        const orderPayload = buildOrderPayload({
          paymentMeta,
          metaExtra: {
            source: "online",
            userRole: isLoggedInUser ? "user" : "guest",
            refundPolicyAgreed: acknowledgedPolicy,
          },
        });

        const { data: savedOrder } = await api.post("/orders", orderPayload);

        if (window.fbq) {
          window.fbq("track", "Purchase", {
            content_ids: discountedCartItems.map((item) => item._id),
            content_type: "product",
            value: totalAmount,
            currency: "CAD",
          });
        }

        if (window.gtag && discountedCartItems.length > 0) {
          window.gtag("event", "purchase", {
            transaction_id: savedOrder._id,
            currency: "CAD",
            value: totalAmount,
            tax: taxAmount,
            shipping: deliveryFee,
            coupon: appliedCoupon || undefined,
            items: discountedCartItems.map((item) => ({
              item_id: item._id,
              item_name: item.displayName || item.name,
              quantity: item.quantity,
              price: item.unitPrice,
              item_category: item.category || "Product",
            })),
          });
        }

        snackbar.success("Order placed successfully!");
        navigate(`/order-confirmation/${savedOrder._id}`);
        clearCart();
      } else if (paymentResult.type === "in-store") {
        const { receiptNumber, passcode, store } = paymentResult;

        const orderPayload = buildOrderPayload({
          paymentMeta: {
            paymentStatus: "succeeded",
            paymentMethod: {
              type: "in_store_pos",
              brand: null,
              last4: null,
            },
          },
          receiptNumber,
          metaExtra: {
            source: "in-store",
            userRole: isLoggedInUser ? "user" : "guest",
            employeePasscode: passcode,
            placedAtLocation: store,
            refundPolicyAgreed: acknowledgedPolicy,
          },
        });

        const { data: savedOrder } = await api.post(
          "/orders/in-store-complete",
          orderPayload
        );

        if (window.fbq) {
          window.fbq("track", "Purchase", {
            content_ids: discountedCartItems.map((item) => item._id),
            content_type: "product",
            value: totalAmount,
            currency: "CAD",
          });
        }

        if (window.gtag && discountedCartItems.length > 0) {
          window.gtag("event", "purchase", {
            transaction_id: savedOrder._id,
            currency: "CAD",
            value: totalAmount,
            tax: taxAmount,
            shipping: deliveryFee,
            coupon: appliedCoupon || undefined,
            items: discountedCartItems.map((item) => ({
              item_id: item._id,
              item_name: item.displayName || item.name,
              quantity: item.quantity,
              price: item.unitPrice,
              item_category: item.category || "Product",
            })),
          });
        }

        snackbar.success("In-store order confirmed successfully!");
        navigate(`/order-confirmation/${savedOrder._id}`);
        clearCart();
      } else {
        snackbar.error("Unknown payment method.");
      }
    } catch (error) {
      snackbar.error("Failed to complete your order. Please try again.");
    }
  };

  return (
    <Container maxWidth="xl" disableGutters sx={{ py: 4, px: 1 }}>
      <Grid container spacing={4}>
        <Grid item xs={12} md={8}>
          <CheckoutStep
            title="1. Delivery Method"
            expanded={activeStep === 1}
            completed={isStep1Complete}
            disabled={false}
            onClick={() => setActiveStep(1)}
            summaryContent={
              <Box display="flex" alignItems="start" gap={1}>
                <CheckCircleIcon
                  color="success"
                  fontSize="small"
                  sx={{ mt: "2px" }}
                />
                <Typography variant="body2" color="text.secondary">
                  {isPickup
                    ? `Pickup at ${selectedLocation}`
                    : address
                    ? `Delivery to ${address}`
                    : "Delivery selected"}
                </Typography>
              </Box>
            }>
            <DeliveryMethodForm
              method={deliveryMethod}
              setMethod={setDeliveryMethod}
              selectedLocation={selectedLocation}
              setSelectedLocation={setSelectedLocation}
              address={address}
              setAddress={setAddress}
              setIsDeliveryValid={setIsDeliveryValid}
            />
          </CheckoutStep>

          <CheckoutStep
            title="2. Schedule Your Order"
            expanded={activeStep === 2}
            completed={isStep2Complete}
            disabled={!isStep1Complete}
            onClick={() => setActiveStep(2)}
            summaryContent={
              <Box display="flex" alignItems="start" gap={1}>
                <CheckCircleIcon
                  color="success"
                  fontSize="small"
                  sx={{ mt: "2px" }}
                />
                <Typography variant="body2" color="text.secondary">
                  {selectedDate?.format("dddd, MMMM D")} at {selectedTime}
                </Typography>
              </Box>
            }>
            <DateTimePicker
              selectedDate={selectedDate}
              onDateChange={setSelectedDate}
              selectedTime={selectedTime}
              onTimeChange={setSelectedTime}
            />
          </CheckoutStep>

          <CheckoutStep
            title="3. Contact Information"
            expanded={activeStep === 3}
            completed={isStep3Complete}
            disabled={!isStep2Complete}
            onClick={() => setActiveStep(3)}
            summaryContent={
              isContactFormValid && (
                <Box display="flex" alignItems="start" gap={1}>
                  <CheckCircleIcon
                    color="success"
                    fontSize="small"
                    sx={{ mt: "3px" }}
                  />
                  <Box>
                    <Typography variant="body2">
                      {contactInfo.firstName} {contactInfo.lastName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {contactInfo.email}, {contactInfo.phone}
                    </Typography>
                  </Box>
                </Box>
              )
            }>
            <ContactInfoForm
              contactInfo={contactInfo}
              setContactInfo={setContactInfo}
              setFormValid={setIsContactFormValid}
            />
          </CheckoutStep>

          <CheckoutStep
            title="4. Payment"
            expanded={activeStep === 4}
            disabled={!isStep3Complete}
            onClick={() => setActiveStep(4)}>
            <CouponCodeInput
              onApply={handleApplyCoupon}
              onRemove={handleRemoveCoupon}
              appliedCode={appliedCoupon}
              error={couponError}
            />

            <PaymentForm
              amount={totalAmount}
              metadata={{
                email: contactInfo.email,
                name: `${contactInfo.firstName} ${contactInfo.lastName}`,
                setAcknowledgment: setAcknowledgedPolicy,
              }}
              onPaymentSuccess={handlePaymentSuccess}
            />
          </CheckoutStep>
        </Grid>

        <Grid item xs={12} md={4} sx={{ position: "relative" }}>
          <Box sx={{ position: "sticky", top: 32 }}>
            <SummarySidebar
              subtotal={cartSubtotal}
              deliveryFee={deliveryFee}
              taxAmount={taxAmount}
              totalAmount={totalAmount}
              appliedCoupon={appliedCoupon}
            />
          </Box>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Checkout;
